import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable } from 'rxjs';
import { PrimeNGToastSeverity } from '../enums/primeng-toast-severity';
import { ConstantsService } from './constants.service';
import { MIMETypeEnum } from '../enums/mime-types';

@Injectable({
    providedIn: 'root',
})
export class FileUploadService {
    private readonly DELETE_FILE_PATH = 'delete-file';
    private readonly UPLOAD_FILE_PATH = 'upload-file';

    constructor(
        private readonly httpClient: HttpClient,
        private readonly toastService: MessageService
    ) {}

    uploadFile(
        file: File,
        apiUrl: string
    ): {
        percentage: Observable<number>;
        blobName: BehaviorSubject<string | null>;
    } {
        let blobNameObservable = new BehaviorSubject<string | null>(null);
        let percentageObservable = new Observable<number>((observer) => {
            this.httpClient
                .post<{ blob_name: string }>(
                    apiUrl + this.UPLOAD_FILE_PATH,
                    file,
                    {
                        headers: this.buildUploadHeaders(file),
                        reportProgress: true,
                        observe: 'events',
                    }
                )
                .subscribe({
                    next: (event) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            // never set 100% here -> wait for final HttpEventType.Response to set 100%
                            let percent = Math.min(
                                Math.round((100 * event.loaded) / event.total!),
                                99
                            );
                            observer.next(percent);
                        } else if (event.type === HttpEventType.Response) {
                            // sending done, set progress to 100%
                            observer.next(100);
                            if (event.body) {
                                blobNameObservable.next(event.body.blob_name);
                            }
                        }
                    },
                    error: (_err) => {
                        this.toastService.add({
                            key: ConstantsService.TOAST_KEY,
                            severity: PrimeNGToastSeverity.ERROR,
                            summary: `Failed to upload file.`,
                            detail: `Please, try again later.`,
                        });
                        observer.next(-1);
                    },
                });
        });
        return {
            percentage: percentageObservable,
            blobName: blobNameObservable,
        };
    }

    deleteFile(blobName: string, apiUrl: string): Observable<boolean> {
        return new Observable<boolean>((observer) => {
            this.httpClient
                .delete(apiUrl + this.DELETE_FILE_PATH, {
                    body: { blob_name: blobName },
                })
                .subscribe({
                    next: () => {
                        observer.next(true);
                        observer.complete();
                    },
                    error: () => {
                        observer.next(false);
                        observer.complete();
                    },
                });
        });
    }

    /**
     * Builds the headers required by the upload worker.
     *
     * Content-Type must be set explicitly: Angular otherwise derives it from the File itself
     * (e.g. application/pdf), which the worker rejects as it only accepts application/octet-stream.
     * x-file-extension is optional for the worker, so it is only sent when the name carries one.
     */
    private buildUploadHeaders(file: File): Record<string, string> {
        const headers: Record<string, string> = {
            'Content-Type': MIMETypeEnum.APPLICATION_OCTET_STREAM,
        };

        const separatorIndex = file.name.lastIndexOf('.');
        if (separatorIndex > 0 && separatorIndex < file.name.length - 1) {
            headers['x-file-extension'] = file.name.slice(separatorIndex + 1);
        }

        return headers;
    }
}
